import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dropdown } from 'react-bootstrap';
import { useWebStore } from '../../context/WebStoreContext';
import CustomerProfileModal from '../ecommerce/CustomerProfileModal';
import { getImageCandidateUrls } from '../../utils/imageHelper';
import { getFontFamilyCss } from '../../utils/fontHelper';

export const resolveItemSpacingCss = (val) => {
  if (!val) return '8px';
  const map = {
    xs: '4px',
    muy_compacto: '4px',
    sm: '6px',
    compacto: '6px',
    md: '8px',
    normal: '8px',
    lg: '12px',
    espaciado: '12px',
    xl: '16px',
    amplio: '16px',
    xxl: '20px',
    muy_amplio: '20px'
  };
  if (map[val]) return map[val];
  if (typeof val === 'number') return `${val}px`;
  if (typeof val === 'string' && /^\d+$/.test(val)) return `${val}px`;
  return val;
};

export const resolveLineHeightCss = (val) => {
  if (!val) return '1.45';
  const map = {
    tight: '1.15',
    muy_compacto: '1.15',
    compact: '1.30',
    compacto: '1.30',
    normal: '1.45',
    relaxed: '1.65',
    holgado: '1.65',
    loose: '1.85',
    amplio: '1.85'
  };
  if (map[val]) return map[val];
  return String(val);
};

export const DEFAULT_NAVBAR_ITEMS = [
  { id: 'home', label: 'INICIO', path: '/', description: 'Página principal de la tienda', icon: 'bi-house-door', enabled: true },
  { id: 'tienda', label: 'TIENDA', path: '/tienda', description: 'Catálogo de productos y compra online', icon: 'bi-shop', enabled: true },
  { id: 'novedades', label: 'NOVEDADES', path: '/novedades', description: 'Lanzamientos y ofertas especiales', icon: 'bi-megaphone', enabled: true },
  { id: 'descargas', label: 'DESCARGAS', path: '/descargas', description: 'Catálogos y listas de precios en PDF', icon: 'bi-cloud-arrow-down', enabled: true },
  { id: 'empresa', label: 'EMPRESA', path: '/empresa', description: 'Historia y planta industrial', icon: 'bi-building', enabled: true },
  { id: 'donde-estamos', label: 'DONDE ESTAMOS', path: '/donde-estamos', description: 'Ubicación de fábrica y sucursales', icon: 'bi-geo-alt', enabled: true },
  { id: 'faq', label: 'PREGUNTAS FRECUENTES', path: '/preguntas-frecuentes', description: 'Centro de ayuda y dudas frecuentes', icon: 'bi-question-circle', enabled: false },
  { id: 'contacto', label: 'CONTACTO', path: '/contacto', description: 'Formulario de contacto y WhatsApp', icon: 'bi-telephone', enabled: true }
];

export const DEFAULT_SECONDARY_NAVBAR = {
  enabled: false,
  visibilityScope: 'all', // 'all' | 'tienda' | 'home'
  backgroundColor: '#bae6fd',
  textColor: '#0f172a',
  hoverTextColor: '#0284c7',
  dropdownBgColor: '#ffffff',
  dropdownTextColor: '#1e293b',
  fontFamily: 'inherit',
  fontSize: 'md', // 'sm' | 'md' | 'lg'
  fontWeight: 'semibold', // 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black'
  alignment: 'center', // 'start' | 'center' | 'between'
  borderBottom: true,
  items: [
    {
      id: 'sec-mas-vendidos',
      label: '¡Los Más Vendidos!',
      enabled: true,
      type: 'direct', // 'direct' | 'rubros_dropdown' | 'megamenu'
      targetType: 'mas_vendidos', // 'mas_vendidos' | 'rubro' | 'search' | 'article' | 'ofertas' | 'faq' | 'url'
      targetValue: '',
      selectedArticles: [],
      highlight: true,
      badgeText: '',
      badgeColor: '#ef4444'
    },
    {
      id: 'sec-rubros-destacados',
      label: 'Rubros Destacados',
      enabled: true,
      type: 'rubros_dropdown',
      rubrosMode: 'all', // 'all' | 'selected'
      selectedRubros: [],
      columnsCount: 3,
      showViewAllLink: true,
      viewAllText: 'Ver más',
      highlight: false,
      badgeText: '',
      fontFamily: 'inherit',
      lineHeight: '1.45',
      itemSpacing: '8px'
    },
    {
      id: 'sec-faq',
      label: 'Preguntas frecuentes',
      enabled: true,
      type: 'direct',
      targetType: 'faq',
      targetValue: '/preguntas-frecuentes',
      highlight: false,
      badgeText: ''
    }
  ]
};

const NAVBAR_ICONS = {
  home: 'bi-house',
  tienda: 'bi-shop',
  novedades: 'bi-megaphone',
  descargas: 'bi-download',
  empresa: 'bi-building',
  'donde-estamos': 'bi-geo-alt',
  faq: 'bi-question-circle',
  contacto: 'bi-telephone'
};

const UnifiedHeader = ({
  activePage = 'home', // 'home' | 'tienda'
  searchTerm = '',
  setSearchTerm,
  cartCount = 0,
  cartTotal = 0,
  currentUser = null,
  onOpenCart,
  onLogout,
  onAuthClick,
  priceListInfo = 'Lista 1 - Minorista',
  canSeePrices = true,
  topBarConfig,
  storeConfig,
  rubros = [],
  onSelectRubro
}) => {
  const navigate = useNavigate();
  const webStoreCtx = useWebStore();
  const landingConfig = webStoreCtx?.landingConfig || {};
  const downloadsSection = landingConfig?.downloadsSection || {};
  const isDownloadsEnabled = downloadsSection.enabled !== false;
  const isShowInNavbar = downloadsSection.showInNavbar !== false;
  const isAuthOnly = downloadsSection.visibility === 'authenticated';
  const showForGuests = (downloadsSection.showInNavbarForGuests !== false) && !isAuthOnly;
  const showDownloadsLink = isDownloadsEnabled && isShowInNavbar && (Boolean(currentUser) || showForGuests);

  const navbarItems = React.useMemo(() => {
    const list = landingConfig?.navbarItems;
    const faqShowInNav = landingConfig?.faqSection?.showInNavbar;
    const faqEnabled = landingConfig?.faqSection?.enabled !== false;
    const baseList = (!Array.isArray(list) || list.length === 0) ? DEFAULT_NAVBAR_ITEMS : list;
    const result = [];
    baseList.forEach(item => {
      const match = DEFAULT_NAVBAR_ITEMS.find(d => d.id === item.id);
      if (match) {
        let isEnabled = item.enabled !== undefined ? item.enabled : match.enabled;
        if (item.id === 'faq') {
          if (!faqEnabled) isEnabled = false;
          else if (faqShowInNav !== undefined) isEnabled = Boolean(faqShowInNav || item.enabled);
        }
        result.push({
          ...match,
          ...item,
          label: item.label || match.label,
          path: item.path || match.path,
          enabled: isEnabled
        });
      } else if (item && item.id) {
        result.push({
          ...item,
          label: item.label || 'ENLACE',
          path: item.path || '/',
          icon: item.icon || 'bi-link-45deg',
          enabled: item.enabled !== false
        });
      }
    });
    DEFAULT_NAVBAR_ITEMS.forEach(defItem => {
      if (!result.some(r => r.id === defItem.id)) {
        let isEnabled = defItem.enabled;
        if (defItem.id === 'faq') {
          isEnabled = faqEnabled && Boolean(faqShowInNav);
        }
        result.push({ ...defItem, enabled: isEnabled });
      }
    });
    return result;
  }, [landingConfig?.navbarItems, landingConfig?.faqSection?.showInNavbar, landingConfig?.faqSection?.enabled]);

  const secondaryNavConfig = React.useMemo(() => {
    const raw = landingConfig?.secondaryNavBar || storeConfig?.secondaryNavBar;
    if (!raw || typeof raw !== 'object') return DEFAULT_SECONDARY_NAVBAR;
    return {
      ...DEFAULT_SECONDARY_NAVBAR,
      ...raw,
      items: Array.isArray(raw.items) ? raw.items : DEFAULT_SECONDARY_NAVBAR.items
    };
  }, [landingConfig?.secondaryNavBar, storeConfig?.secondaryNavBar]);

  const isSecondaryNavVisible = React.useMemo(() => {
    if (!secondaryNavConfig?.enabled) return false;
    const scope = secondaryNavConfig.visibilityScope || 'all';
    if (scope === 'tienda' && activePage !== 'tienda') return false;
    if (scope === 'home' && activePage !== 'home') return false;
    const activeItems = (secondaryNavConfig.items || []).filter(i => i && i.enabled !== false);
    return activeItems.length > 0;
  }, [secondaryNavConfig, activePage]);

  const [openSecMenuId, setOpenSecMenuId] = useState(null);
  const [fetchedRubros, setFetchedRubros] = useState([]);
  const [fetchedFamilias, setFetchedFamilias] = useState([]);
  const secMenuCloseTimerRef = React.useRef(null);
  const secBarRef = React.useRef(null);

  const activeCompanyId = useMemo(() => {
    return (
      webStoreCtx?.activeCompanyId ||
      landingConfig?.company_id ||
      storeConfig?.company_id ||
      null
    );
  }, [webStoreCtx?.activeCompanyId, landingConfig?.company_id, storeConfig?.company_id]);

  // Asegurar que UnifiedHeader tenga siempre rubros y sus familias (mfam000) en todas las páginas
  useEffect(() => {
    let isMounted = true;
    const loadCatalogMeta = async () => {
      try {
        const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
        const params = activeCompanyId ? { company: activeCompanyId } : {};
        const requests = [
          axios.get('/api/familias/', { params, headers }).catch(() => ({ data: [] }))
        ];
        if (!Array.isArray(rubros) || rubros.length === 0) {
          requests.push(axios.get('/api/rubros/', { params, headers }).catch(() => ({ data: [] })));
        }
        const [resFams, resRubs] = await Promise.all(requests);
        if (!isMounted) return;
        const famsList = Array.isArray(resFams?.data) ? resFams.data : (resFams?.data?.results || []);
        setFetchedFamilias(famsList);
        if (resRubs) {
          const rubsList = Array.isArray(resRubs?.data) ? resRubs.data : (resRubs?.data?.results || []);
          setFetchedRubros(rubsList);
        }
      } catch (e) {
        // ignore
      }
    };
    loadCatalogMeta();
    return () => {
      isMounted = false;
    };
  }, [activeCompanyId, rubros]);

  const effectiveRubros = useMemo(() => {
    const rawBaseRubros = (Array.isArray(rubros) && rubros.length > 0) ? rubros : fetchedRubros;
    if (!Array.isArray(rawBaseRubros) || rawBaseRubros.length === 0) return [];

    // Excluir rubros inhabilitados (c_situa === 9), rubro 'PRODUCTOS VIEJOS' o sin artículos activos
    const baseRubros = rawBaseRubros.filter(r => {
      if (!r) return false;
      if (r.c_situa === 9 || Number(r.c_situa) === 9) return false;
      if (r.q_articulos !== undefined && Number(r.q_articulos) <= 0) return false;
      const name = String(r.d_rubro || '').trim().toLowerCase();
      if (name === 'productos viejos') return false;
      return true;
    });

    const famsByRubro = {};
    (fetchedFamilias || []).forEach(f => {
      const cr = String(f.c_rubro || '').trim();
      const cf = String(f.c_famil || '').trim();
      const df = String(f.d_famil || '').trim();
      if (!cr || !cf || !df) return;
      if (!famsByRubro[cr]) famsByRubro[cr] = [];
      if (!famsByRubro[cr].some(x => String(x.c_famil).trim() === cf)) {
        famsByRubro[cr].push({ c_famil: cf, d_famil: df, c_rubro: cr });
      }
    });

    return baseRubros.map(r => {
      const cr = String(r.c_rubro || '').trim();
      const existingFams = Array.isArray(r.familias) ? r.familias : [];
      const extraFams = famsByRubro[cr] || [];
      const mergedMap = new Map();
      [...existingFams, ...extraFams].forEach(fm => {
        const cf = String(fm?.c_famil || '').trim();
        const df = String(fm?.d_famil || '').trim();
        if (cf && df && !mergedMap.has(cf)) {
          mergedMap.set(cf, { c_famil: cf, d_famil: df, c_rubro: cr });
        }
      });
      const mergedFams = Array.from(mergedMap.values()).sort((a, b) =>
        (a.d_famil || '').localeCompare(b.d_famil || '', 'es-AR')
      );
      return {
        ...r,
        familias: mergedFams
      };
    });
  }, [rubros, fetchedRubros, fetchedFamilias]);

  const handleSecItemMouseEnter = (itemId) => {
    if (typeof window !== 'undefined' && window.innerWidth < 992) return;
    if (secMenuCloseTimerRef.current) {
      clearTimeout(secMenuCloseTimerRef.current);
      secMenuCloseTimerRef.current = null;
    }
    setOpenSecMenuId(itemId);
  };

  const handleSecItemMouseLeave = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 992) return;
    if (secMenuCloseTimerRef.current) clearTimeout(secMenuCloseTimerRef.current);
    secMenuCloseTimerRef.current = setTimeout(() => {
      setOpenSecMenuId(null);
    }, 180);
  };

  const resolveSecondaryTargetUrl = (targetType, targetValue, label = '', selectedArticles = [], parentRubro = '') => {
    const tType = targetType || 'url';
    if (tType === 'familia') {
      if (targetValue && typeof targetValue === 'object') {
        const rCode = String(targetValue.rubro || targetValue.c_rubro || parentRubro || '').trim();
        const fCode = String(targetValue.familia || targetValue.c_famil || '').trim();
        if (rCode && fCode) {
          return `/tienda?rubro=${encodeURIComponent(rCode)}&familia=${encodeURIComponent(fCode)}`;
        }
        if (fCode) {
          return `/tienda?familia=${encodeURIComponent(fCode)}`;
        }
      }
      const rawVal = String(targetValue || '').trim();
      if (rawVal.includes('|')) {
        const [rPart, fPart] = rawVal.split('|').map(s => s.trim());
        if (rPart && fPart) {
          return `/tienda?rubro=${encodeURIComponent(rPart)}&familia=${encodeURIComponent(fPart)}`;
        }
      }
      if (parentRubro && rawVal) {
        return `/tienda?rubro=${encodeURIComponent(String(parentRubro).trim())}&familia=${encodeURIComponent(rawVal)}`;
      }
      return rawVal ? `/tienda?familia=${encodeURIComponent(rawVal)}` : '/tienda';
    }

    const tVal = String(targetValue || '').trim();
    if (tType === 'mas_vendidos') {
      return '/tienda?mas_vendidos=1';
    }
    if (tType === 'ofertas') {
      return '/tienda?ofertas=1';
    }
    if (tType === 'faq') {
      return tVal || '/preguntas-frecuentes';
    }
    if (tType === 'rubro') {
      return tVal ? `/tienda?rubro=${encodeURIComponent(tVal)}` : '/tienda';
    }
    if (tType === 'search') {
      const q = tVal || String(label || '').trim();
      if (parentRubro && q) {
        return `/tienda?rubro=${encodeURIComponent(String(parentRubro).trim())}&q=${encodeURIComponent(q)}`;
      }
      return q ? `/tienda?q=${encodeURIComponent(q)}` : '/tienda';
    }
    if (tType === 'article') {
      if (Array.isArray(selectedArticles) && selectedArticles.length > 0) {
        const codes = selectedArticles.map(a => a.c_artic || a.id).filter(Boolean).join(',');
        if (codes) {
          return `/tienda?ids=${encodeURIComponent(codes)}${label ? `&titulo=${encodeURIComponent(label)}` : ''}`;
        }
      }
      if (tVal) {
        return `/tienda?ids=${encodeURIComponent(tVal)}${label ? `&titulo=${encodeURIComponent(label)}` : ''}`;
      }
      return '/tienda';
    }
    return tVal || '/tienda';
  };

  const handleNavigateTarget = (targetType, targetValue, label = '', selectedArticles = [], parentRubro = '') => {
    setOpenSecMenuId(null);
    setIsNavOpen(false);
    setIsSearchOpen(false);

    if (targetType === 'rubro' && targetValue && activePage === 'tienda' && onSelectRubro) {
      onSelectRubro(targetValue);
    }

    const url = resolveSecondaryTargetUrl(targetType, targetValue, label, selectedArticles, parentRubro);
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cerrar mega-menú al hacer clic fuera o presionar Escape
  React.useEffect(() => {
    const handleDocClick = (e) => {
      if (openSecMenuId && secBarRef.current && !secBarRef.current.contains(e.target)) {
        setOpenSecMenuId(null);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape' && openSecMenuId) {
        setOpenSecMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      window.removeEventListener('keydown', handleEsc);
    };
  }, [openSecMenuId]);

  const [localSearch, setLocalSearch] = useState(searchTerm || '');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = React.useRef(null);

  // Auto focus al abrir el buscador
  React.useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isSearchOpen]);

  // Cerrar buscador con tecla Escape
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  // Cerrar menú mobile al cambiar de página
  React.useEffect(() => {
    setIsNavOpen(false);
  }, [activePage]);

  // Sincronizar valor local de búsqueda si cambia desde props
  React.useEffect(() => {
    if (searchTerm !== undefined) {
      setLocalSearch(searchTerm);
    }
  }, [searchTerm]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setIsNavOpen(false);
    setIsSearchOpen(false);
    const query = localSearch.trim();
    if (activePage === 'tienda') {
      if (setSearchTerm) setSearchTerm(query);
    } else {
      if (query) {
        navigate(`/tienda?q=${encodeURIComponent(query)}`);
      } else {
        navigate('/tienda');
      }
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setLocalSearch(val);
    if (activePage === 'tienda' && setSearchTerm) {
      setSearchTerm(val);
    }
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    if (activePage === 'tienda' && setSearchTerm) {
      setSearchTerm('');
    }
  };

  const handleSelectCategory = (rubro) => {
    setIsNavOpen(false);
    setIsSearchOpen(false);
    if (onSelectRubro) {
      onSelectRubro(rubro.c_rubro);
    }
    if (activePage !== 'tienda') {
      navigate(`/tienda?rubro=${encodeURIComponent(rubro.c_rubro)}`);
    }
  };

  const handleLoginClick = () => {
    setIsNavOpen(false);
    if (onAuthClick) {
      onAuthClick('login');
    } else {
      navigate('/login?redirect=/tienda');
    }
  };

  const handleRegisterClick = () => {
    setIsNavOpen(false);
    if (onAuthClick) {
      onAuthClick('register');
    } else {
      navigate('/login?redirect=/tienda');
    }
  };

  const rawLogoUrl = landingConfig?.logoUrl || storeConfig?.logoUrl || '';
  const logoCandidates = useMemo(() => getImageCandidateUrls(rawLogoUrl), [rawLogoUrl]);
  const [logoCandidateIndex, setLogoCandidateIndex] = useState(0);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoCandidateIndex(0);
    setLogoFailed(false);
  }, [rawLogoUrl]);

  const logoText = landingConfig?.logoText || storeConfig?.logoText || storeConfig?.footer?.companyName || '';
  const showLogoText = landingConfig?.showLogoText !== false && storeConfig?.showLogoText !== false;
  const logoSubtitle = landingConfig?.logoSubtitle !== undefined
    ? landingConfig.logoSubtitle
    : (storeConfig?.logoSubtitle !== undefined ? storeConfig.logoSubtitle : 'FÁBRICA & TIENDA OFICIAL');
  const showLogoSubtitle = landingConfig?.showLogoSubtitle !== false && storeConfig?.showLogoSubtitle !== false && Boolean(logoSubtitle && String(logoSubtitle).trim());
  const displayName = currentUser?.d_razon || currentUser?.nombre || currentUser?.username || 'Mi Cuenta';
  const effectivePriceListInfo = currentUser?.nombre_lista || (currentUser?.n_lista ? `Lista ${currentUser.n_lista}` : priceListInfo) || 'Lista 1 - Minorista';
  const phone = topBarConfig?.phone || storeConfig?.footer?.phone || '(011) 4247-6622';
  const whatsapp = topBarConfig?.whatsapp || storeConfig?.footer?.whatsapp || '5491142476622';
  const announcement = topBarConfig?.announcementText || '🚀 ¡Venta Mayorista y Minorista con Envíos a Todo el País! | Consultas por WhatsApp';

  return (
    <header className="unified-header-wrapper position-sticky top-0" style={{ zIndex: 1040 }}>
      
      {/* 1. TOP ANNOUNCEMENT BAR */}
      {topBarConfig?.enabled !== false && (
        <div 
          className="py-2 px-3 small border-bottom"
          style={{ 
            fontSize: '0.82rem',
            backgroundColor: 'var(--ecom-topbar-bg, #0f172a)',
            color: 'var(--ecom-topbar-text, #ffffff)',
            borderColor: 'rgba(255,255,255,0.15)'
          }}
        >
          <div className="container-fluid px-3 px-lg-4 d-flex flex-column flex-md-row align-items-center justify-content-between text-center text-md-start gap-1">
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-warning text-dark fw-bold px-2 py-1 rounded-pill flex-shrink-0" style={{ fontSize: '0.7rem' }}>OFICIAL</span>
              <span className="opacity-90" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{announcement}</span>
            </div>
            <div className="d-flex align-items-center gap-3 opacity-90 d-none d-md-flex">
              {phone && (
                <a href={`tel:${phone.replace(/\D/g, '')}`} className="text-decoration-none hover-opacity" style={{ color: 'var(--ecom-topbar-text, #ffffff)' }}>
                  <i className="bi bi-telephone-fill text-warning me-1"></i> {phone}
                </a>
              )}
              {whatsapp && (
                <a 
                  href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-decoration-none hover-opacity"
                  style={{ color: 'var(--ecom-topbar-text, #ffffff)' }}
                >
                  <i className="bi bi-whatsapp text-success me-1"></i> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2. MAIN NAVBAR */}
      <nav 
        className="navbar navbar-expand-xl shadow-sm py-2 border-bottom position-relative"
        style={{
          backgroundColor: 'var(--ecom-navbar-bg, #ffffff)',
          color: 'var(--ecom-navbar-text, #1e293b)',
          borderColor: 'var(--ecom-header-border, #e2e8f0)',
          minHeight: '62px'
        }}
      >
        <div className="container-fluid px-3 px-lg-4">
          
          {/* Logo & Marca */}
          <a 
            href="/" 
            className="navbar-brand d-flex align-items-center me-3 text-decoration-none"
            style={{ gap: '14px' }}
            onClick={(e) => {
              e.preventDefault();
              setIsNavOpen(false);
              navigate('/');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            {logoCandidates.length > 0 && !logoFailed ? (
              <img 
                src={logoCandidates[logoCandidateIndex]} 
                alt={logoText || 'Logo'} 
                style={{ height: '38px', width: 'auto', maxHeight: '38px', maxWidth: '140px', objectFit: 'contain', flexShrink: 0 }}
                onError={() => {
                  if (logoCandidateIndex + 1 < logoCandidates.length) {
                    setLogoCandidateIndex(prev => prev + 1);
                  } else {
                    setLogoFailed(true);
                  }
                }}
              />
            ) : (
              <div 
                className="bg-primary text-white rounded-2 d-flex align-items-center justify-content-center fw-bold shadow-xs flex-shrink-0" 
                style={{ width: '38px', height: '38px', fontSize: '1.1rem' }}
              >
                {logoText ? logoText.charAt(0).toUpperCase() : <i className="bi bi-building"></i>}
              </div>
            )}
            {(showLogoText || showLogoSubtitle) && (
              <div className="d-flex flex-column lh-1">
                {showLogoText && (
                  <span className="fw-black fs-5 text-primary text-nowrap" style={{ fontWeight: 800, letterSpacing: '-0.3px' }}>
                    {logoText || 'SISGECOM'}
                  </span>
                )}
                {showLogoSubtitle && (
                  <span className="text-muted fw-semibold text-nowrap d-none d-sm-block mt-1" style={{ fontSize: '0.65rem', letterSpacing: '0.8px' }}>
                    {logoSubtitle}
                  </span>
                )}
              </div>
            )}
          </a>

          {/* Acciones Rápidas Mobile & Tablet (< 1200px): Lupa + Carrito + Hamburguesa */}
          <div className="d-flex align-items-center gap-2 d-xl-none ms-auto">
            <button
              type="button"
              className="btn btn-light border rounded-circle d-flex align-items-center justify-content-center text-dark shadow-xs p-0"
              style={{ width: '36px', height: '36px' }}
              onClick={() => {
                setIsSearchOpen(true);
                setIsNavOpen(false);
              }}
              title="Buscar productos o ID"
              aria-label="Buscar productos o ID"
            >
              <i className="bi bi-search fs-6"></i>
            </button>

            {/* Botón Carrito Mobile & Tablet */}
            <button
              type="button"
              className="btn btn-warning btn-sm rounded-pill px-3 py-2 fw-bold text-dark shadow-xs d-flex align-items-center gap-2 flex-shrink-0"
              onClick={onOpenCart}
              title="Ver mi carrito de compras"
              style={{ minHeight: '36px' }}
            >
              <i className="bi bi-cart3 fs-6"></i>
              {cartCount > 0 && (
                <span className="badge bg-danger text-white rounded-pill px-2 py-1" style={{ fontSize: '0.7rem' }}>
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>

            <button 
              className="btn btn-light border rounded-3 p-2 text-dark shadow-xs d-flex align-items-center justify-content-center" 
              type="button" 
              onClick={() => setIsNavOpen(prev => !prev)}
              aria-controls="unifiedNavbarContent"
              aria-expanded={isNavOpen}
              aria-label="Abrir menú de navegación"
              style={{ width: '36px', height: '36px' }}
            >
              <i className={`bi ${isNavOpen ? 'bi-x-lg' : 'bi-list'} fs-5 text-dark`}></i>
            </button>
          </div>

          {/* Contenido Colapsable */}
          <div className={`collapse navbar-collapse ${isNavOpen ? 'show pt-3 pb-2' : ''}`} id="unifiedNavbarContent">
            
            {/* Enlaces Principales Dinámicos */}
            <ul className="navbar-nav d-flex flex-column flex-xl-row align-items-stretch align-items-xl-center mb-3 mb-xl-0 fw-bold gap-1 gap-xxl-1 me-xl-1" style={{ fontSize: '0.82rem', letterSpacing: '0.3px' }}>
              {navbarItems
                .filter(item => {
                  if (item.enabled === false) return false;
                  if (item.id === 'descargas') return showDownloadsLink;
                  return true;
                })
                .map(item => {
                  const isActive = activePage === item.id;
                  return (
                    <li key={item.id} className="nav-item">
                      <a
                        className={`nav-link text-nowrap px-3 px-xxl-3 py-2 py-xl-1 rounded-pill ${
                          isActive ? 'active text-primary fw-extrabold bg-primary-subtle' : 'text-dark'
                        }`}
                        href={item.path}
                        onClick={(e) => {
                          e.preventDefault();
                          setIsNavOpen(false);
                          if (/^https?:\/\//i.test(item.path)) {
                            window.open(item.path, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          navigate(item.path);
                          if (item.path !== '/tienda') {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }}
                      >
                        {item.label}
                      </a>
                    </li>
                  );
                })}
            </ul>

            {/* Buscador Integrado para Pantallas Ultra Anchas (Monitores 27"+ >= 1650px) */}
            <form className="header-search-bar-desktop flex-grow-1 mx-2 my-0" onSubmit={handleSearchSubmit} style={{ maxWidth: '240px' }}>
              <div className="position-relative w-100 d-flex align-items-center">
                <input 
                  type="text" 
                  className="form-control form-control-sm bg-light border-0 py-2 ps-3 pe-4 text-dark shadow-none" 
                  placeholder="Buscar..." 
                  value={localSearch}
                  onChange={handleSearchChange}
                  style={{ 
                    fontSize: '0.82rem', 
                    borderRadius: '20px',
                    backgroundColor: '#f1f5f9'
                  }}
                />
                <button 
                  type="submit" 
                  className="btn position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent text-muted p-0 me-2 d-flex align-items-center justify-content-center hover-opacity"
                  style={{ width: '22px', height: '22px' }}
                  title="Buscar"
                >
                  <i className="bi bi-search" style={{ fontSize: '0.85rem' }}></i>
                </button>
              </div>
            </form>

            {/* Accesos Rápidos: Búsqueda (Notebooks/Laptops/Tablets), Usuario y Carrito Desktop */}
            <div className="d-flex align-items-center gap-2 ms-xl-auto mt-2 mt-xl-0 flex-nowrap flex-shrink-0">
              
              {/* Botón Lupa de Búsqueda (Visible en notebooks 15/17" y pantallas < 1650px) */}
              <button
                type="button"
                className="btn btn-light border rounded-circle header-search-btn-lupa align-items-center justify-content-center text-dark shadow-xs"
                style={{ width: '38px', height: '38px', flexShrink: 0 }}
                onClick={() => setIsSearchOpen(true)}
                title="Buscar productos o ID"
                aria-label="Buscar productos o ID"
              >
                <i className="bi bi-search fs-6"></i>
              </button>
              
              {/* Usuario / Login */}
              {currentUser ? (
                <Dropdown align="end">
                  <Dropdown.Toggle
                    as="div"
                    className="btn btn-light border rounded-pill px-3 py-2 fw-semibold d-flex align-items-center gap-2 shadow-xs dropdown-toggle-no-caret"
                    id="dropdown-header-user"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '28px', height: '28px', fontSize: '0.8rem' }}>
                      <i className="bi bi-person-fill"></i>
                    </div>
                    <div className="d-flex flex-column text-start lh-1" style={{ paddingRight: '2px' }}>
                      <span className="small text-truncate fw-bold" style={{ maxWidth: '115px' }}>
                        {displayName}
                      </span>
                      <span className="text-muted text-truncate mt-1" style={{ fontSize: '0.64rem' }}>
                        {effectivePriceListInfo}
                      </span>
                    </div>
                    <i className="bi bi-chevron-down text-muted" style={{ fontSize: '0.72rem', marginLeft: '4px' }}></i>
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="shadow border-0 rounded-3 mt-2 p-2" style={{ minWidth: '240px' }}>
                    <div className="px-3 py-2 border-bottom mb-1">
                      <p className="mb-0 fw-bold small text-dark">{displayName}</p>
                      {currentUser.d_email && (
                        <p className="mb-0 text-muted small text-truncate" style={{ fontSize: '0.75rem' }}>
                          <i className="bi bi-envelope me-1"></i>{currentUser.d_email}
                        </p>
                      )}
                      {currentUser.d_whatsapp && (
                        <p className="mb-0 text-muted small" style={{ fontSize: '0.75rem' }}>
                          <i className="bi bi-whatsapp text-success me-1"></i>{currentUser.d_whatsapp}
                        </p>
                      )}
                      <span className="badge bg-primary-subtle text-primary border border-primary-subtle mt-1" style={{ fontSize: '0.7rem' }}>
                        {effectivePriceListInfo}
                      </span>
                    </div>
                    <Dropdown.Item
                      className="small d-flex align-items-center gap-2 py-2 rounded-2"
                      onClick={() => {
                        setIsNavOpen(false);
                        navigate('/tienda');
                      }}
                    >
                      <i className="bi bi-bag-check text-primary"></i> Mi Catálogo Online
                    </Dropdown.Item>
                    <Dropdown.Item
                      className="small d-flex align-items-center gap-2 py-2 rounded-2"
                      onClick={() => {
                        setIsNavOpen(false);
                        setShowProfileModal(true);
                      }}
                    >
                      <i className="bi bi-person-gear text-primary"></i> Mis Datos / Perfil
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      className="text-danger small d-flex align-items-center gap-2 py-2 rounded-2"
                      onClick={() => {
                        setIsNavOpen(false);
                        if (onLogout) onLogout();
                      }}
                    >
                      <i className="bi bi-box-arrow-right"></i> Cerrar Sesión
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-2 fw-semibold shadow-xs text-nowrap"
                    onClick={handleLoginClick}
                    title="Iniciá sesión para ver tus precios y listas asignadas"
                  >
                    <i className="bi bi-person-fill"></i>
                    <span>Ingresar</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-2 fw-semibold shadow-xs text-nowrap"
                    onClick={handleRegisterClick}
                    title="Creá tu cuenta comercial en segundos"
                  >
                    <i className="bi bi-person-plus-fill"></i>
                    <span>Registrarme</span>
                  </button>
                </div>
              )}

              {/* Botón Carrito de Compras (Desktop / Pantallas Grandes y Medianas) */}
              <button
                type="button"
                className="btn btn-warning btn-sm rounded-pill px-3 py-2 fw-bold text-dark shadow-xs d-flex align-items-center gap-2 flex-shrink-0 text-nowrap"
                onClick={onOpenCart}
                title="Ver mi carrito de pedidos"
                style={{ height: '38px' }}
              >
                <i className="bi bi-cart3 fs-6"></i>
                <span className="small">Carrito</span>
                {cartCount > 0 && (
                  <span className="badge bg-danger text-white rounded-pill px-2 py-1" style={{ fontSize: '0.75rem' }}>
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>

            </div>

          </div>

        </div>

        {/* MOBILE COLLAPSIBLE MENU */}
        {isNavOpen && (
          <div className="d-lg-none w-100 border-top mt-2 pt-2 px-3 pb-3 bg-white animate-fade-in shadow-sm">
            {/* Topbar Info en Mobile */}
            {topBarConfig && (topBarConfig.phone || topBarConfig.email) && (
              <div className="d-flex flex-column gap-2 py-2 px-3 mb-2 bg-light rounded-3 small text-muted">
                {topBarConfig.phone && (
                  <a href={`tel:${topBarConfig.phone.replace(/\D/g, '')}`} className="text-decoration-none text-muted d-flex align-items-center gap-2">
                    <i className="bi bi-telephone text-primary"></i>
                    <span>{topBarConfig.phone}</span>
                  </a>
                )}
                {topBarConfig.email && (
                  <a href={`mailto:${topBarConfig.email}`} className="text-decoration-none text-muted d-flex align-items-center gap-2">
                    <i className="bi bi-envelope text-primary"></i>
                    <span>{topBarConfig.email}</span>
                  </a>
                )}
              </div>
            )}

            {/* Nav Links Dinámicos */}
            <div className="d-flex flex-column gap-1 mb-3">
              {navbarItems
                .filter(item => {
                  if (item.enabled === false) return false;
                  if (item.id === 'descargas') return showDownloadsLink;
                  return true;
                })
                .map(item => {
                  const isActive = activePage === item.id;
                  const icon = item.icon || NAVBAR_ICONS[item.id] || 'bi-link-45deg';
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`btn text-start d-flex align-items-center gap-3 py-2 px-3 rounded-3 fw-semibold ${
                        isActive ? 'bg-primary text-white' : 'text-dark hover-bg-light'
                      }`}
                      onClick={() => {
                        setIsNavOpen(false);
                        if (/^https?:\/\//i.test(item.path)) {
                          window.open(item.path, '_blank', 'noopener,noreferrer');
                          return;
                        }
                        navigate(item.path);
                        if (item.path !== '/tienda') {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      <i className={`bi ${icon} fs-5`}></i>
                      <span>{item.label}</span>
                    </button>
                  );
                })}

              {/* Acceso Carrito en Menú Mobile */}
              <button
                type="button"
                className="btn text-start d-flex align-items-center justify-content-between py-2 px-3 rounded-3 fw-bold bg-warning-subtle text-dark border border-warning-subtle mt-1"
                onClick={() => {
                  setIsNavOpen(false);
                  onOpenCart();
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <i className="bi bi-cart3 fs-5 text-warning-emphasis"></i>
                  <span>Mi Carrito de Pedidos</span>
                </div>
                {cartCount > 0 && (
                  <span className="badge bg-danger text-white rounded-pill px-2 py-1" style={{ fontSize: '0.75rem' }}>
                    {cartCount > 99 ? '99+' : cartCount} {cartCount === 1 ? 'artículo' : 'artículos'}
                  </span>
                )}
              </button>
            </div>

            {/* Auth en Mobile */}
            <div className="pt-2 border-top">
              {currentUser ? (
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex align-items-center gap-3 p-2 bg-light rounded-3">
                    <div 
                      className="rounded-circle bg-primary text-white fw-bold d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '36px', height: '36px', fontSize: '0.9rem' }}
                    >
                      {(currentUser.first_name || currentUser.username || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="fw-bold text-dark text-truncate">{currentUser.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}` : currentUser.username}</div>
                      <div className="text-muted small text-truncate" style={{ fontSize: '0.75rem' }}>{currentUser.email}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2 py-2 rounded-3 fw-semibold"
                    onClick={() => {
                      setIsNavOpen(false);
                      setShowProfileModal(true);
                    }}
                  >
                    <i className="bi bi-person-gear"></i>
                    <span>Mis Datos / Perfil</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2 py-2 rounded-3 fw-semibold"
                    onClick={() => {
                      setIsNavOpen(false);
                      if (onLogout) onLogout();
                    }}
                  >
                    <i className="bi bi-box-arrow-right"></i>
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              ) : (
                <div className="d-grid gap-2">
                  <button
                    type="button"
                    className="btn btn-primary w-100 py-2 rounded-3 fw-bold text-white shadow-xs"
                    onClick={handleLoginClick}
                  >
                    Iniciar Sesión
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100 py-2 rounded-3 fw-bold"
                    onClick={handleRegisterClick}
                  >
                    Crear Cuenta
                  </button>
                </div>
              )}
            </div>

          </div>
        )}

        {/* OVERLAY MODAL / MODAL DE BÚSQUEDA RÁPIDA */}
        {isSearchOpen && (
          <div 
            className="search-modal-backdrop position-fixed top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center p-3 p-md-4"
            style={{ 
              backgroundColor: 'rgba(15, 23, 42, 0.65)', 
              zIndex: 1060,
              backdropFilter: 'blur(4px)',
              overflowY: 'auto'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsSearchOpen(false);
              }
            }}
          >
            <div 
              className="search-modal-card bg-white rounded-4 shadow-2xl border w-100 overflow-hidden d-flex flex-column my-auto"
              style={{ 
                maxWidth: '640px',
                animation: 'ecomFadeInDown 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header del Buscador: Input y Botones */}
              <form onSubmit={handleSearchSubmit} className="p-3 p-md-4 border-bottom bg-white m-0">
                <div className="d-flex align-items-center gap-2">
                  <div className="position-relative flex-grow-1 d-flex align-items-center">
                    <i 
                      className="bi bi-search position-absolute start-0 ms-3 text-primary" 
                      style={{ fontSize: '1.2rem', pointerEvents: 'none' }}
                    ></i>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="form-control form-control-lg border-2 border-primary-subtle py-2 text-dark shadow-none"
                      style={{ 
                        paddingLeft: '44px',
                        paddingRight: localSearch ? '78px' : '40px',
                        fontSize: '1rem',
                        borderRadius: '12px',
                        backgroundColor: '#f8fafc'
                      }}
                      placeholder="Buscar por nombre, código o ID (ej: 100, Plato...)"
                      value={localSearch}
                      onChange={handleSearchChange}
                    />
                    <div className="position-absolute end-0 me-2 d-flex align-items-center gap-1">
                      {localSearch && (
                        <button
                          type="button"
                          className="btn border-0 bg-transparent text-muted p-1 d-flex align-items-center justify-content-center"
                          style={{ width: '28px', height: '28px' }}
                          onClick={handleClearSearch}
                          title="Limpiar texto"
                        >
                          <i className="bi bi-x-circle-fill fs-5"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary rounded-3 px-3 py-2 fw-bold text-white d-flex align-items-center gap-2 flex-shrink-0 shadow-xs"
                    style={{ minHeight: '44px' }}
                  >
                    <i className="bi bi-search"></i>
                    <span className="d-none d-sm-inline">Buscar</span>
                  </button>

                  <button
                    type="button"
                    className="btn btn-light border rounded-3 p-2 text-dark d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '44px', height: '44px' }}
                    onClick={() => setIsSearchOpen(false)}
                    title="Cerrar (Esc)"
                  >
                    <i className="bi bi-x-lg fs-5"></i>
                  </button>
                </div>
              </form>

              {/* Cuerpo del Buscador: Categorías sugeridas & Tip de búsqueda */}
              <div className="p-3 p-md-4 bg-light" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                
                {localSearch.trim() && (
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-primary w-100 py-2 fw-bold rounded-3 shadow-xs d-flex align-items-center justify-content-center gap-2"
                      onClick={handleSearchSubmit}
                    >
                      <i className="bi bi-search"></i>
                      <span>Ver resultados para "{localSearch}"</span>
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  </div>
                )}

                {rubros && rubros.length > 0 && (
                  <div className="mb-3">
                    <span className="text-muted small fw-bold text-uppercase d-block mb-3" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                      Categorías destacadas
                    </span>
                    <div className="d-flex flex-wrap" style={{ gap: '8px 10px' }}>
                      {rubros.slice(0, 10).map(r => (
                        <button
                          key={r.c_rubro}
                          type="button"
                          className="btn btn-white bg-white border btn-sm rounded-pill text-dark px-3 py-2 fw-semibold shadow-2xs hover-bg-light text-nowrap d-inline-flex align-items-center"
                          style={{ fontSize: '0.8rem', lineHeight: '1.2' }}
                          onClick={() => handleSelectCategory(r)}
                        >
                          <i className="bi bi-tag text-primary me-2"></i>
                          {r.d_rubro || `Rubro ${r.c_rubro}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-3 bg-white border rounded-3 text-secondary d-flex align-items-start gap-3 shadow-2xs" style={{ borderRadius: '12px' }}>
                  <i className="bi bi-lightbulb-fill fs-5 text-warning mt-1 flex-shrink-0"></i>
                  <div className="small lh-sm">
                    <div className="fw-bold text-dark mb-1">Búsqueda rápida y precisa</div>
                    <div className="text-muted">
                      Ingresá el número de ID (ej: <b>100</b>), código de barras o palabras clave del producto y presioná <b>Enter</b> o <b>Buscar</b> para encontrarlo al instante.
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}
      </nav>

      {/* 3. BARRA SECUNDARIA / MEGA-MENÚ COMERCIAL MODULAR */}
      {isSecondaryNavVisible && (() => {
        const activeBarItems = (secondaryNavConfig.items || []).filter(i => i && i.enabled !== false && String(i.label || '').trim() !== '');
        if (activeBarItems.length === 0) return null;

        const barBg = secondaryNavConfig.backgroundColor || '#bae6fd';
        const barText = secondaryNavConfig.textColor || '#0f172a';
        const dropBg = secondaryNavConfig.dropdownBgColor || '#ffffff';
        const dropText = secondaryNavConfig.dropdownTextColor || '#1e293b';

        const fontSizeMap = { sm: '0.8rem', md: '0.88rem', lg: '0.96rem' };
        const baseFontSize = fontSizeMap[secondaryNavConfig.fontSize] || '0.88rem';

        const weightMap = { normal: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800, black: 900 };
        const baseFontWeight = weightMap[secondaryNavConfig.fontWeight] || 600;

        const barFontFamily = getFontFamilyCss(secondaryNavConfig.fontFamily);

        const justifyClass = secondaryNavConfig.alignment === 'start'
          ? 'justify-content-start'
          : secondaryNavConfig.alignment === 'between'
          ? 'justify-content-between'
          : 'justify-content-center';

        const openItem = activeBarItems.find(
          it => it.id === openSecMenuId && (it.type === 'rubros_dropdown' || it.type === 'megamenu')
        );

        const itemFontFamily = getFontFamilyCss(openItem?.fontFamily) || barFontFamily;
        const itemLineHeight = resolveLineHeightCss(openItem?.lineHeight);
        const itemSpacing = resolveItemSpacingCss(openItem?.itemSpacing);

        return (
          <div
            ref={secBarRef}
            className={`secondary-category-navbar position-relative ${secondaryNavConfig.borderBottom !== false ? 'border-bottom' : ''}`}
            style={{
              backgroundColor: barBg,
              color: barText,
              borderColor: 'rgba(15, 23, 42, 0.1)',
              zIndex: 1035,
              ...(barFontFamily ? { fontFamily: barFontFamily } : {})
            }}
          >
            <style>{`
              .sec-nav-item-btn {
                transition: opacity 0.15s ease, transform 0.15s ease, background-color 0.15s ease;
              }
              .sec-nav-item-btn:hover {
                background-color: rgba(255, 255, 255, 0.28);
              }
              .sec-megamenu-sublink {
                transition: color 0.15s ease, transform 0.15s ease;
              }
              .sec-megamenu-sublink:hover {
                color: var(--ecom-primary, #0284c7) !important;
                text-decoration: underline !important;
              }
            `}</style>

            <div className="container-fluid px-3 px-lg-4">
              <div
                className={`d-flex align-items-center ${justifyClass} flex-nowrap overflow-auto py-2`}
                style={{
                  gap: '6px 18px',
                  scrollbarWidth: 'none'
                }}
              >
                {activeBarItems.map((item, idx) => {
                  const itemId = item.id || `sec-item-${idx}`;
                  const isDropdown = item.type === 'rubros_dropdown' || item.type === 'megamenu';
                  const isOpen = openSecMenuId === itemId;
                  const itemWeight = item.highlight ? Math.max(800, baseFontWeight) : baseFontWeight;
                  const itemColor = item.customTextColor || barText;

                  return (
                    <div
                      key={itemId}
                      className="flex-shrink-0"
                      onMouseEnter={() => isDropdown ? handleSecItemMouseEnter(itemId) : setOpenSecMenuId(null)}
                      onMouseLeave={() => isDropdown ? handleSecItemMouseLeave() : undefined}
                    >
                      <button
                        type="button"
                        className="btn border-0 sec-nav-item-btn px-3 py-1 rounded-pill d-inline-flex align-items-center gap-2 text-nowrap shadow-none"
                        style={{
                          fontSize: baseFontSize,
                          fontWeight: itemWeight,
                          color: itemColor,
                          backgroundColor: isOpen ? 'rgba(255, 255, 255, 0.38)' : 'transparent',
                          ...(barFontFamily ? { fontFamily: barFontFamily } : {})
                        }}
                        onClick={() => {
                          if (isDropdown) {
                            setOpenSecMenuId(prev => (prev === itemId ? null : itemId));
                          } else {
                            handleNavigateTarget(
                              item.targetType || 'url',
                              item.targetValue || '',
                              item.label || '',
                              item.selectedArticles || []
                            );
                          }
                        }}
                      >
                        {item.badgeText && (
                          <span
                            className="badge rounded-pill px-2 py-1 fw-bold"
                            style={{
                              backgroundColor: item.badgeColor || '#ef4444',
                              color: '#ffffff',
                              fontSize: '0.66rem'
                            }}
                          >
                            {item.badgeText}
                          </span>
                        )}
                        <span>{item.label}</span>
                        {isDropdown && (
                          <i
                            className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}
                            style={{ fontSize: '0.74rem', opacity: 0.85 }}
                          ></i>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* PANEL DESPLEGABLE / MEGA-MENÚ CON ESTILO TARJETA FLOTANTE REDONDEADA (COMO EN IMAGEN 2) */}
            {openItem && (
              <div
                className="w-100 px-2 px-md-4"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  zIndex: 1050,
                  pointerEvents: 'none'
                }}
              >
                <div
                  className="container position-relative"
                  style={{
                    maxWidth: '1160px',
                    pointerEvents: 'auto'
                  }}
                  onMouseEnter={() => handleSecItemMouseEnter(openItem.id)}
                  onMouseLeave={handleSecItemMouseLeave}
                >
                  {/* Flechita superior blanca tipo tooltip (como en Imagen 2) */}
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: '10px solid transparent',
                      borderRight: '10px solid transparent',
                      borderBottom: `10px solid ${dropBg}`,
                      marginLeft: '140px',
                      filter: 'drop-shadow(0 -2px 2px rgba(15, 23, 42, 0.06))'
                    }}
                  />

                  <div
                    className="shadow-lg border"
                    style={{
                      backgroundColor: dropBg,
                      color: dropText,
                      borderRadius: '18px',
                      borderColor: 'rgba(15, 23, 42, 0.08)',
                      boxShadow: '0 18px 42px rgba(15, 23, 42, 0.16)',
                      padding: '26px 30px',
                      maxHeight: '75vh',
                      overflowY: 'auto',
                      ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                    }}
                  >
                    {/* CASO 1: LISTA DESPLEGABLE DE RUBROS Y SUS FAMILIAS / SUB-FAMILIAS */}
                    {openItem.type === 'rubros_dropdown' && (() => {
                      const mode = openItem.rubrosMode || 'all';
                      const selectedCodes = Array.isArray(openItem.selectedRubros)
                        ? openItem.selectedRubros.map(c => String(c).trim())
                        : [];

                      let rubrosToShow = [];
                      if (mode === 'selected' && selectedCodes.length > 0) {
                        selectedCodes.forEach(code => {
                          const found = (effectiveRubros || []).find(
                            r => String(r.c_rubro).trim() === code || String(r.d_rubro || '').trim().toLowerCase() === code.toLowerCase()
                          );
                          if (found) {
                            rubrosToShow.push(found);
                          } else {
                            rubrosToShow.push({ c_rubro: code, d_rubro: code, familias: [] });
                          }
                        });
                      } else {
                        rubrosToShow = effectiveRubros || [];
                      }

                      const showDbFamilias = openItem.showDbFamilias !== false;
                      const excludedByRubro = openItem.excludedFamiliasByRubro || {};
                      const customSubsByRubro = openItem.customSubItemsByRubro || {};

                      // Para cada rubro, armar sus sub-familias (de la BD mfam000 + las agregadas manualmente)
                      const enrichedRubros = rubrosToShow.map(r => {
                        const cRub = String(r.c_rubro || '').trim();
                        const excluded = Array.isArray(excludedByRubro[cRub])
                          ? excludedByRubro[cRub].map(x => String(x).trim())
                          : [];

                        const dbSubItems = showDbFamilias && Array.isArray(r.familias)
                          ? r.familias
                              .filter(f => f && f.c_famil && !excluded.includes(String(f.c_famil).trim()))
                              .map(f => ({
                                id: `dbfam-${cRub}-${f.c_famil}`,
                                label: f.d_famil,
                                targetType: 'familia',
                                targetValue: { rubro: cRub, familia: f.c_famil },
                                parentRubro: cRub
                              }))
                          : [];

                        const customSubs = Array.isArray(customSubsByRubro[cRub])
                          ? customSubsByRubro[cRub]
                              .filter(s => s && s.enabled !== false && String(s.label || '').trim() !== '')
                              .map(s => ({
                                ...s,
                                parentRubro: cRub
                              }))
                          : [];

                        return {
                          ...r,
                          c_rubro_clean: cRub,
                          subItems: [...dbSubItems, ...customSubs]
                        };
                      });

                      const rubrosWithFamilies = enrichedRubros.filter(r => r.subItems && r.subItems.length > 0);
                      const standaloneRubros = enrichedRubros.filter(r => !r.subItems || r.subItems.length === 0);

                      const colsCount = Number(openItem.columnsCount) || 3;
                      const colBootstrapClass = colsCount === 1
                        ? 'col-12 col-md-6'
                        : colsCount === 2
                        ? 'col-12 col-sm-6'
                        : colsCount === 4
                        ? 'col-12 col-sm-6 col-lg-3'
                        : 'col-12 col-sm-6 col-lg-4';

                      return (
                        <div>
                          {openItem.dropdownTitle && (
                            <h6 className="fw-bold mb-3 pb-2 border-bottom" style={{ color: dropText, ...(itemFontFamily ? { fontFamily: itemFontFamily } : {}) }}>
                              {openItem.dropdownTitle}
                            </h6>
                          )}

                          {enrichedRubros.length === 0 ? (
                            <div className="text-muted small py-2">
                              No hay rubros disponibles para mostrar.
                            </div>
                          ) : rubrosWithFamilies.length > 0 ? (
                            /* DISEÑO IMAGEN 2: Columna izquierda para rubros directos + Columnas de Familia (Negrita) y Sub-familias debajo */
                            <div className="row g-4 align-items-stretch">
                              {standaloneRubros.length > 0 && (
                                <div
                                  className="col-12 col-md-3"
                                  style={{ borderRight: '1px solid #e5e7eb', paddingRight: '20px' }}
                                >
                                  <div className="d-flex flex-column" style={{ gap: itemSpacing }}>
                                    {standaloneRubros.map((r, rIdx) => (
                                      <button
                                        key={r.c_rubro || rIdx}
                                        type="button"
                                        className="btn btn-link p-0 text-start text-decoration-none sec-megamenu-sublink"
                                        style={{
                                          color: '#6b7280',
                                          fontSize: '0.96rem',
                                          fontWeight: 400,
                                          lineHeight: itemLineHeight,
                                          ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                        }}
                                        onClick={() => handleNavigateTarget('rubro', r.c_rubro, r.d_rubro)}
                                      >
                                        {r.d_rubro || `Rubro ${r.c_rubro}`}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className={standaloneRubros.length > 0 ? 'col-12 col-md-9' : 'col-12'}>
                                <div className="row g-4">
                                  {rubrosWithFamilies.map((r, rIdx) => (
                                    <div
                                      key={r.c_rubro || rIdx}
                                      className={colBootstrapClass}
                                      style={
                                        rIdx < rubrosWithFamilies.length - 1
                                          ? { borderRight: '1px solid #f1f5f9' }
                                          : undefined
                                      }
                                    >
                                      <div className="d-flex flex-column h-100 pe-2">
                                        {/* Nombre de la Familia / Rubro Padre en Negrita */}
                                        <button
                                          type="button"
                                          className="btn btn-link p-0 text-start text-decoration-none fw-bold mb-3 sec-megamenu-sublink"
                                          style={{
                                            color: '#52525b',
                                            fontSize: '1rem',
                                            letterSpacing: '-0.1px',
                                            lineHeight: itemLineHeight,
                                            ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                          }}
                                          onClick={() => handleNavigateTarget('rubro', r.c_rubro, r.d_rubro)}
                                        >
                                          {r.d_rubro || `Rubro ${r.c_rubro}`}
                                        </button>

                                        {/* Sub-familias debajo */}
                                        <div className="d-flex flex-column" style={{ gap: itemSpacing }}>
                                          {r.subItems.map((sub, sIdx) => (
                                            <button
                                              key={sub.id || sIdx}
                                              type="button"
                                              className="btn btn-link p-0 text-start text-decoration-none sec-megamenu-sublink"
                                              style={{
                                                color: '#6b7280',
                                                fontSize: '0.94rem',
                                                fontWeight: 400,
                                                lineHeight: itemLineHeight,
                                                ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                              }}
                                              onClick={() =>
                                                handleNavigateTarget(
                                                  sub.targetType || 'familia',
                                                  sub.targetValue || sub.label || '',
                                                  sub.label || '',
                                                  sub.selectedArticles || [],
                                                  r.c_rubro_clean
                                                )
                                              }
                                            >
                                              {sub.label}
                                            </button>
                                          ))}
                                        </div>

                                        {openItem.showViewAllLink !== false && (
                                          <div className="mt-3 pt-1">
                                            <button
                                              type="button"
                                              className="btn btn-link p-0 text-start text-decoration-none sec-megamenu-sublink"
                                              style={{
                                                color: '#64748b',
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                lineHeight: itemLineHeight,
                                                ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                              }}
                                              onClick={() => handleNavigateTarget('rubro', r.c_rubro, r.d_rubro)}
                                            >
                                              {openItem.viewAllText || 'Ver más'}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Si ningún rubro tiene sub-familias cargadas aún, mostrarlos en columnas limpias */
                            <div className="row g-3">
                              {standaloneRubros.map((r, rIdx) => (
                                <div key={r.c_rubro || rIdx} className={colBootstrapClass}>
                                  <button
                                    type="button"
                                    className="btn btn-link text-decoration-none p-2 text-start w-100 d-flex align-items-center gap-2 sec-megamenu-sublink"
                                    style={{
                                      color: '#52525b',
                                      fontSize: '0.95rem',
                                      fontWeight: 600,
                                      lineHeight: itemLineHeight,
                                      ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                    }}
                                    onClick={() => handleNavigateTarget('rubro', r.c_rubro, r.d_rubro)}
                                  >
                                    <span>{r.d_rubro || `Rubro ${r.c_rubro}`}</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* CASO 2: MEGA-MENÚ CON SUB-SECCIONES / COLUMNAS (COMO EN LA IMAGEN 2) */}
                    {openItem.type === 'megamenu' && (() => {
                      const activeCols = (openItem.columns || []).filter(c => c && c.enabled !== false);
                      const leftLinks = (openItem.leftLinks || []).filter(l => l && l.enabled !== false && String(l.label || '').trim() !== '');

                      if (activeCols.length === 0 && leftLinks.length === 0) {
                        return (
                          <div className="text-muted small py-2 text-center">
                            No hay columnas configuradas en este menú desplegable.
                          </div>
                        );
                      }

                      const colClass = activeCols.length === 1
                        ? 'col-12 col-md-6'
                        : activeCols.length === 2
                        ? 'col-12 col-md-6'
                        : activeCols.length === 3
                        ? 'col-12 col-md-4'
                        : 'col-12 col-sm-6 col-lg-3';

                      return (
                        <div className="row g-4 align-items-stretch">
                          {leftLinks.length > 0 && (
                            <div
                              className="col-12 col-md-3"
                              style={{ borderRight: '1px solid #e5e7eb', paddingRight: '20px' }}
                            >
                              <div className="d-flex flex-column" style={{ gap: itemSpacing }}>
                                {leftLinks.map((lnk, lIdx) => (
                                  <button
                                    key={lnk.id || lIdx}
                                    type="button"
                                    className="btn btn-link p-0 text-start text-decoration-none sec-megamenu-sublink"
                                    style={{
                                      color: '#6b7280',
                                      fontSize: '0.96rem',
                                      fontWeight: 400,
                                      lineHeight: itemLineHeight,
                                      ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                    }}
                                    onClick={() =>
                                      handleNavigateTarget(
                                        lnk.targetType || 'search',
                                        lnk.targetValue || lnk.label || '',
                                        lnk.label || '',
                                        lnk.selectedArticles || []
                                      )
                                    }
                                  >
                                    {lnk.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className={leftLinks.length > 0 ? 'col-12 col-md-9' : 'col-12'}>
                            <div className="row g-4">
                              {activeCols.map((col, cIdx) => {
                                const activeSubs = (col.subItems || []).filter(s => s && s.enabled !== false && String(s.label || '').trim() !== '');
                                const hasColTarget = Boolean(col.targetValue && String(col.targetValue).trim() !== '');
                                const parentRub = col.targetType === 'rubro' ? col.targetValue : '';

                                return (
                                  <div
                                    key={col.id || cIdx}
                                    className={colClass}
                                    style={
                                      cIdx < activeCols.length - 1
                                        ? { borderRight: '1px solid #f1f5f9' }
                                        : undefined
                                    }
                                  >
                                    <div className="d-flex flex-column h-100 pe-2">
                                      {/* Título de la Familia / Columna en Negrita */}
                                      {col.title && (
                                        hasColTarget ? (
                                          <button
                                            type="button"
                                            className="btn btn-link p-0 text-start text-decoration-none fw-bold mb-3 sec-megamenu-sublink"
                                            style={{
                                              color: '#52525b',
                                              fontSize: '1rem',
                                              lineHeight: itemLineHeight,
                                              ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                            }}
                                            onClick={() => handleNavigateTarget(col.targetType || 'rubro', col.targetValue, col.title)}
                                          >
                                            {col.title}
                                          </button>
                                        ) : (
                                          <h6
                                            className="fw-bold mb-3"
                                            style={{
                                              color: '#52525b',
                                              fontSize: '1rem',
                                              lineHeight: itemLineHeight,
                                              ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                            }}
                                          >
                                            {col.title}
                                          </h6>
                                        )
                                      )}

                                      {/* Lista de Sub-Familias de la Columna */}
                                      <div className="d-flex flex-column" style={{ gap: itemSpacing }}>
                                        {activeSubs.map((sub, sIdx) => (
                                          <button
                                            key={sub.id || sIdx}
                                            type="button"
                                            className="btn btn-link p-0 text-start text-decoration-none sec-megamenu-sublink"
                                            style={{
                                              color: '#6b7280',
                                              fontSize: '0.94rem',
                                              fontWeight: 400,
                                              lineHeight: itemLineHeight,
                                              ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                            }}
                                            onClick={() =>
                                              handleNavigateTarget(
                                                sub.targetType || 'search',
                                                sub.targetValue || sub.label || '',
                                                sub.label || '',
                                                sub.selectedArticles || [],
                                                parentRub
                                              )
                                            }
                                          >
                                            {sub.label}
                                          </button>
                                        ))}
                                      </div>

                                      {/* Link "Ver más" al pie de la columna */}
                                      {col.showMoreLink !== false && (
                                        <div className="mt-3 pt-1">
                                          <button
                                            type="button"
                                            className="btn btn-link p-0 text-start text-decoration-none sec-megamenu-sublink"
                                            style={{
                                              color: '#64748b',
                                              fontSize: '0.85rem',
                                              fontWeight: 600,
                                              lineHeight: itemLineHeight,
                                              ...(itemFontFamily ? { fontFamily: itemFontFamily } : {})
                                            }}
                                            onClick={() =>
                                              handleNavigateTarget(
                                                col.targetType || (col.targetValue ? 'rubro' : 'url'),
                                                col.targetValue || '/tienda',
                                                col.title || ''
                                              )
                                            }
                                          >
                                            {col.moreLinkText || 'Ver más'}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}
      
      {/* Modal de Edición de Perfil de Cliente */}
      {currentUser && (
        <CustomerProfileModal
          show={showProfileModal}
          onHide={() => setShowProfileModal(false)}
          currentUser={currentUser}
        />
      )}

    </header>
  );
};

export default UnifiedHeader;
